/**
 * 用户中心相关API接口
 */
import request from './utils/request'

/**
 * 退出企业管理员
 * @param {number} enterpriseId - 当前管理的企业ID
 * @returns {Promise}
 */
export function exitEnterpriseAdmin(enterpriseId) {
  return request({
    url: '/web/enterprise/exitEnterpriseAdmin',
    method: 'post',
    params: { enterpriseId }
  })
}

/**
 * 更新用户基本信息
 * @param {Object} data - 用户信息数据
 * @param {string} data.nickName - 昵称
 * @param {string} data.realName - 真实姓名
 * @param {string} data.phone - 手机号码
 * @param {string} data.email - 电子邮件
 * @returns {Promise} 更新结果
 */
export function updateUserNickName(nickName) {
  return request({
    url: '/web/portal/userCenter/updateCurrentPersonalUser',
    method: 'post',
    params: {
      nickName: nickName
    }
  })
}

/**
 * 更新当前登录账号的头像
 * @param {string} avatar - 头像URL
 * @returns {Promise} 更新结果
 */
export function updateCurrentAvatar(avatar) {
  return request({
    url: '/web/portal/userCenter/updateCurrentAvatar',
    method: 'post',
    params: {
      avatar: avatar
    }
  })
}

/**
 * 提交个人身份认证
 * @param {Object} data - 身份认证数据
 * @param {string} data.realName - 真实姓名
 * @param {string} data.idCardNumber - 身份证号码
 * @param {string} data.idCardFront - 身份证正面照片URL
 * @param {string} data.idCardBack - 身份证反面照片URL
 * @param {string} data.phone - 手机号码
 * @param {string} data.email - 电子邮件
 * @returns {Promise} 提交结果
 */
export function submitPersonalAuthentication(data) {
  return request({
    url: '/web/portal/userCenter/personalUserAuthentication',
    method: 'post',
    data: data
  })
}

/**
 * 提交企业身份认证
 * @param {Object} data - 企业身份认证数据
 * @param {string} data.businessLicense - 营业执照URL
 * @param {string} data.entName - 企业名称
 * @param {string} data.creditCode - 统一社会信用代码
 * @param {string} data.linkmanName - 联系人名称
 * @param {string} data.linkmanPhone - 联系人号码
 * @param {string} data.linkmanEmail - 联系人邮箱
 * @param {string} data.inTheArea - 所在地区
 * @param {string} data.industry - 所属行业
 * @param {string} data.isVerifiedType - 企业认证类型（10大陆企业 20港澳台企业 30外籍企业 40协会|机构|组织）
 * @returns {Promise} 提交结果
 */
export function submitEnterpriseAuthentication(data) {
  return request({
    url: '/web/enterprise/enterpriseCreateCertification',
    method: 'post',
    data: data
  })
}

/**
 * 企业重新提交认证
 * @param {Object} data - 企业身份认证数据
 * @param {string} data.businessLicense - 营业执照URL
 * @param {string} data.entName - 企业名称
 * @param {string} data.creditCode - 统一社会信用代码
 * @param {string} data.linkmanName - 联系人名称
 * @param {string} data.linkmanPhone - 联系人号码
 * @param {string} data.linkmanEmail - 联系人邮箱
 * @param {string} data.inTheArea - 所在地区
 * @param {string} data.industry - 所属行业
 * @param {string} data.isVerifiedType - 企业认证类型（10大陆企业 20港澳台企业 30外籍企业 40协会|机构|组织）
 * @returns {Promise} 提交结果
 */
export function submitEnterpriseReCertification(data) {
  return request({
    url: '/web/enterprise/enterpriseReCertification',
    method: 'post',
    data: data
  })
}

/**
 * 根据超级管理员ID获取企业信息列表（个人主体账号使用）
 * 后端已改为返回数组：一人可创建多家企业，按创建时间倒序
 * @returns {Promise<{ code: number, data: Array }>} 企业信息列表
 */
export function getEnterpriseBySuperAdminId() {
  return request({
    url: '/web/portal/userCenter/getEnterpriseBySuperAdminId',
    method: 'get'
  })
}

/**
 * 获取当前企业的管理员列表
 */
export function getCurrentEnterpriseAdminList() {
  return request({
    url: '/web/enterprise/getCurrentEnterpriseAdminList',
    method: 'get'
  });
}

/**
 * 根据身份证号和手机号查询用户个人信息
 * @param {string} idCardNo - 身份证号
 * @param {string} phoneNumber - 手机号
 * @returns {Promise} 用户信息
 */
export function getUserByIdCardAndPhone(idCardNo, phoneNumber) {
  return request({
    url: '/web/portal/userCenter/getUserByIdCardAndPhone',
    method: 'get',
    params: {
      idCardNo: idCardNo,
      phoneNumber: phoneNumber
    }
  });
}

/**
 * 根据统一社会信用代码和联系人电话查询企业信息
 * @param {string} creditCode - 统一社会信用代码
 * @param {string} linkmanPhone - 联系人电话
 * @returns {Promise} 企业信息
 */
export function getEnterpriseByCreditCodeAndPhone(creditCode, linkmanPhone) {
  return request({
    url: '/web/portal/userCenter/getEnterpriseByCreditCodeAndPhone',
    method: 'get',
    params: {
      creditCode: creditCode,
      linkmanPhone: linkmanPhone
    }
  });
}

/**
 * 移除管理员
 * @param {Array<number>} id - 角色表组件id
 * @returns {Promise} 移除结果
 */
export function removeAdminById(id) {
  return request({
    url: '/web/enterprise/removeAdminById',
    method: 'post',
    data: id
  });
}

/**
 * 修改管理员状态
 * @param {Object} data - 管理员数据 { id, status }
 * @param {number} data.id - 角色表ID
 * @param {string} data.status - 状态：0-正常，1-停用
 * @returns {Promise} 修改结果
 */
export function editAdminStatus(data) {
  return request({
    url: '/web/enterprise/editAdminStatus',
    method: 'post',
    data: data
  });
}

/**
 * 更换超级管理员
 * @param {Object} data - 数据 { userId, smsCode }
 * @returns {Promise} 结果
 */
export function replaceSuperAdmin(data) {
  return request({
    url: '/web/enterprise/replaceSuperAdmin',
    method: 'post',
    data: data
  });
}

/**
 * 获取渠道企业列表
 * @returns {Promise} 渠道企业列表，包含渠道名称和渠道ID
 */
export function getChannelEnterpriseList() {
  return request({
    url: '/web/portal/userCenter/getChannelEnterpriseList',
    method: 'get'
  })
}

/**
 * 获取专项治理一级渠道企业列表
 * @returns {Promise} 一级渠道企业列表
 */
export function getPrimaryChannelEnterpriseList() {
  return request({
    url: '/web/portal/userCenter/getPrimaryChannelEnterpriseList',
    method: 'get'
  })
}

/**
 * 加入渠道企业
 * @param {number} channelId - 渠道ID
 * @param {string} channelCode - 渠道码
 * @returns {Promise} 加入结果
 */
export function joinChannelEnterprise(channelId, channelCode) {
  return request({
    url: '/web/portal/portalSysProxyRelation/joinChannel',
    method: 'post',
    params: {
      channelId: channelId,
      channelCode: channelCode
    }
  })
}

/**
 * 获取代理关系信息
 * @returns {Promise} 代理关系信息，包括是否授权机构代理、是否加入渠道、是否授权渠道代理
 */
export function getProxyRelationInfo() {
  return request({
    url: '/web/portal/portalSysProxyRelation/getProxyRelationInfo',
    method: 'get'
  })
}

/**
 * 用户自主退出或解除授权代理
 * @param {string} agentEntId - 代理企业ID
 * @returns {Promise} 操作结果
 */
export function clientQuit(agentEntId) {
  return request({
    url: '/web/portal/portalSysProxyRelation/clientQuit',
    method: 'post',
    params: {
      agentEntId: agentEntId
    }
  })
}



/**
 * 用户自主取消渠道代理
 * @param {string} agentEntId - 代理企业ID
 * @returns {Promise} 操作结果
 */
export function clientCancelChannelAgency(agentEntId) {
  return request({
    url: '/web/portal/portalSysProxyRelation/clientCancelChannelAgency',
    method: 'post',
    params: {
      agentEntId: agentEntId
    }
  })
}



/**
 * OCR证件识别（通过图片文件）
 * @param {FormData} formData - 包含 file 和 cardType 的表单数据
 * @returns {Promise} 识别结果
 */
export function recognizeCardByFile(formData) {
  return request({
    url: '/web/portal/ocr/recognizeByFile',
    method: 'post',
    data: formData,
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  })
}

/**
 * 重置当前登录账号的密码
 * @param {string} phoneNumber - 超级管理员手机号
 * @param {string} smsCode - 验证码
 * @param {string} newPassword - 新密码
 * @returns {Promise} 结果
 */
export function resetPassword(phoneNumber, smsCode, newPassword) {
  return request({
    url: '/web/enterprise/resetPassword',
    method: 'post',
    params: {
      phoneNumber: phoneNumber,
      smsCode: smsCode,
      newPassword: newPassword
    }
  })
}
